import React, { Component, Fragment } from 'react';
import { action, computed, extendObservable, observable, toJS } from 'mobx';
import { observer, inject, Observer } from 'mobx-react';
import moment from 'moment-timezone';
import _ from 'lodash';

import { CommandBar } from 'office-ui-fabric-react/lib/CommandBar';
import { DetailsList, Selection } from 'office-ui-fabric-react/lib/DetailsList';
import { Image, ImageFit } from 'office-ui-fabric-react/lib/Image';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { Panel, PanelType } from 'office-ui-fabric-react/lib/Panel';
import { Link } from 'office-ui-fabric-react/lib/Link';
import {
  PrimaryButton,
  DefaultButton,
} from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';

import { localizable } from 'components/Localization';
import Loading from 'components/LoadingOverlay';
import Money from 'components/Money';
import GuideOverlay from 'components/GuideOverlay';
import dynamicImport from 'components/dynamicImport';
import SaleCreatePanel from 'components/SaleCreatePanel';
import BehalfOfAgentsDropdown from 'components/BehalfOfAgentsDropdown';
import { getQueryStringValue } from 'helpers/queryString';
import Documents from './Documents';

import { get } from 'services/request';
import { TermsLabel } from '../../components/TermsLabel';

const SearchPanelContent = dynamicImport({
  component: '/containers/Search/SearchPanelContent',
});

@inject('inventory', 'sale', 'app', 'user')
@localizable
@observer
class Search extends Component {
  @observable toc = false;
  @observable openNewSalePanel = false;

  @observable creating = false;
  creationAbortController; // = new AbortController()

  @observable mls = '';
  @observable masterSaleId = '';
  @observable selfRegistration = false;
  @observable isTest = false;

  @observable documents = []; // [ { id, title, fileName, type, url, _disabled, _selected, _error, _uploaded: {} } ]

  @computed get selectedDocumentsCount() {
    return _.filter(this.documents, (doc) => doc._selected).length;
  }
  @observable processedDocumentsCount = 0;
  @computed get documentUploadErrorsCount() {
    return _.filter(this.documents, (doc) => doc._error).length;
  }

  @computed get tocDisabled() {
    const config = this.props.user.userConfig;

    if (!config.saleCreateRequireTC) {
      return true;
    }

    return (
      typeof config.disableSaleToC !== 'undefined' &&
      config.disableSaleToC.includes(this.props.user.profile.tenant)
    );
  }

  constructor(props) {
    super(props);
    this.selection = new Selection({
      onSelectionChanged: this.handleSelectionChanged,
    });
  }

  async componentDidMount() {
    const { inventory, history, t } = this.props;

    const propertyId =
      getQueryStringValue('property') || getQueryStringValue('search');

    this.isTest = getQueryStringValue('test') === '1';

    inventory.initColumns(t, this.handleColumnHeaderClick);

    if (!!propertyId) {
      const existing = await inventory.getExistingSale(propertyId);

      if (existing) {
        history.replace(`/dashboard/${existing}`);
        return;
      }

      inventory.get(propertyId).then(
        () => {
          if (!inventory.isListed && !!inventory.selectedItem)
            this.handleItemSelected();
        },
        () => {}
      );
    }

    inventory.search();
    await inventory.loadAgents();
  }

  async componentDidUpdate(prevProps) {
    const propertyId =
      getQueryStringValue('property') || getQueryStringValue('search');
    if (!propertyId) return;

    const { inventory, history } = this.props;

    if (!propertyId) return;

    const listed =
      inventory.listed || (await inventory.findListing(propertyId));

    if (!listed) return;

    const invite = getQueryStringValue('invite');
    if (invite && invite.length > 0) {
      const inviteDecoded = decodeURI(invite);
      const contact = {};

      inviteDecoded.split(',').forEach((keyValue) => {
        const [prop, value] = keyValue.split(':');
        if (prop && value) contact[prop] = value;
      });

      if (Object.keys(contact).length !== 0) {
        sessionStorage.setItem(
          'invite',
          JSON.stringify({
            panel: 'registration',
            payload: contact,
          })
        );
      }
    }

    const contactId = getQueryStringValue('contactIds');
    if (contactId && contactId.length > 0) {
      sessionStorage.setItem(
        'invite',
        JSON.stringify({
          panel: 'bulkRegistrationCrm',
          payload: contactId,
        })
      );
    }

    history.replace(`/dashboard/${listed.id}`);
  }

  handleBack = () => {
    const { history } = this.props;
    history.push(`/dashboard`);
  };

  @action handleAddNew = async () => {
    const { inventory, sale, app, user, t } = this.props;
    const { id, inventoryId } = inventory.selectedItem;

    this.creating = true;
    this.processedDocumentsCount = 0;

    this.documents.forEach((doc) => {
      doc._error = null;
    });

    this.creationAbortController =
      typeof window.AbortController !== 'undefined'
        ? new window.AbortController()
        : null;
    try {
      const agentData = toJS(inventory.saleAgent);
      const saleFeatures = app.getSaleFeatures({
        test: !!this.isTest,
        isMasterSale: this.masterSaleId === 'master',
        disableAutomaticReminders:
          !!this.masterSaleId && this.masterSaleId !== 'master',
        selfRegistration: !!this.selfRegistration,
      });
      const offerData = {
        ...(user.userConfig.defaultSaleOfferData || {}),
        ...inventory.selectedItem.private,
      };
      const command = {
        referenceId: (id || '').toString(),
        masterSaleId:
          !this.masterSaleId || this.masterSaleId === 'master'
            ? undefined
            : this.masterSaleId,
        itemData: Object.assign({}, inventory.selectedItem.public, {
          agent: agentData,
        }),
        offerData,
        agentData,
        integrations: inventory.selectedItem.integrations,
        offerForm: offerData.offer_form,
        docs: [],
        mls: this.mls,
        features: saleFeatures,
      };

      //TODO: command.offerData.sale_country from inventory?
      if (
        !command.offerData.sale_country &&
        !!app.data &&
        !!app.data.sale_country
      )
        command.offerData.sale_country = app.data.sale_country;

      if (this.selectedDocumentsCount > 0) {
        const inventoryUrl =
          app.config.inventory.url +
          (app.config.inventory.url.endsWith('/') ? '' : '/');

        for (var i = 0; i < this.documents.length; i++) {
          const doc = this.documents[i];
          if (!doc._disabled && doc._selected) {
            this.processedDocumentsCount++;

            if (!doc._uploaded) {
              // document not yet retrieved from Inventory, so try to retrieve it now

              // if the returned document URL contains '://' consider it an absolute URL,
              // if not, consider it relative to the Inventory URL
              const url = doc.url.includes('://')
                ? doc.url
                : inventoryUrl + doc.url;
              try {
                const file = await get(
                  url,
                  user.accessToken,
                  null,
                  this.creationAbortController
                    ? this.creationAbortController.signal
                    : null
                );
                if (file.url)
                  doc._uploaded = {
                    id: file.id || doc.id,
                    originalFilename: doc.fileName,
                    url: file.url,
                    type: doc.type,
                    uploadedAt: moment.utc().format(),
                    short: doc.type ? t(`documenttype::${doc.type}`) : '',
                    full: doc.type
                      ? t(
                          `documenttype::${doc.type}_long`,
                          {},
                          `documenttype::${doc.type}`
                        )
                      : '',
                    fileName: doc.type,
                  };
              } catch (error) {
                if (
                  !this.creationAbortController ||
                  !this.creationAbortController.signal.aborted
                )
                  doc._error =
                    t('Error retrieving file') +
                    (typeof error.response !== 'undefined'
                      ? ` (${error.response})`
                      : '');
              }
            }

            if (doc._uploaded) command.docs.push(doc._uploaded);
          }

          if (
            this.creationAbortController &&
            this.creationAbortController.signal.aborted
          )
            break;
        }
      }

      if (this.documentUploadErrorsCount === 0) {
        if (
          !this.creationAbortController ||
          !this.creationAbortController.signal.aborted
        ) {
          this.toc = false;
          inventory.deselect();
          await sale.start(command);
          if (app.features && app.features.deleteSaleFromInventory)
            await inventory.delete(inventoryId);
        }
      }
    } finally {
      this.creating = false;
      this.creationAbortController = null;
    }
  };

  handleRenderObjectColumn = (object, index, column) => {
    const { intl } = this.props;
    const content = object[column.fieldName];
    switch (column.key) {
      case 'image':
        return (
          <Image
            src={content}
            width={50}
            height={50}
            imageFit={ImageFit.cover}
          />
        );
      case 'price':
        return (
          <Money
            value={content}
            currency={this.props.app.currency.code} //TODO: currency code could be taken from the corresponding sale
            locale={this.props.intl.locale}
          />
        );
      case 'area':
        return <span>{intl.formatNumber(content)}</span>;
      default:
        return <span>{content}</span>;
    }
  };

  handleSelectionChanged = () => {
    this.documents.clear();

    const item = this.selection.getSelection()[0];
    if (!item) {
      return;
    }
    const { inventory } = this.props;
    this.toc = false;
    inventory.select(item).then(() => {
      this.handleItemSelected();
    });
  };

  isDocAllowed = (doc) => {
    if (!doc.fileName) return true;
    const extension = doc.fileName.includes('.')
      ? doc.fileName.substr(doc.fileName.lastIndexOf('.') + 1)
      : '';
    return ['JPG', 'PNG', 'PDF'].includes(extension.toUpperCase());
  };

  handleItemSelected = () => {
    const { inventory } = this.props;
    const config = this.props.user.userConfig;

    this.documents.clear();
    this.mls =
      config.mlsTypes && config.mlsTypes.length > 0 ? config.mlsTypes[0] : '';
    if (inventory.selectedItem && inventory.selectedItem.docs) {
      inventory.selectedItem.docs.forEach((doc) => {
        if (!doc.type) {
          // use title or fileName if no type could be inferred
          if (doc.title) doc.type = doc.title;
          else if (doc.fileName)
            doc.type = doc.fileName.includes('.')
              ? doc.fileName.substr(0, doc.fileName.lastIndexOf('.'))
              : doc.fileName;
        }
        this.documents.push(
          extendObservable(observable(doc), {
            _disabled: !this.isDocAllowed(doc),
            _selected: false, // this.isDocAllowed(doc),
            _error: null,
          })
        );
      });
    }
    if (inventory.agents.length === 1)
      inventory.selectedAgent = inventory.agents[0];
    if (inventory.agents.length > 1) {
      const itemAgent = inventory.agents.find(
        (a) => a.agent_id === inventory.selectedItem.public.agent.id
      );
      inventory.selectedAgent = !!itemAgent ? itemAgent : null;
    }
  };

  handleDocumentSelectionChanged = (id, selected) => {
    this.documents.forEach((doc) => {
      if (doc.id === id && !doc._disabled) doc._selected = selected;
    });
  };

  handleColumnHeaderClick = (e, column) => {
    const { inventory } = this.props;
    inventory.sortBy(column.fieldName);
  };

  handleOpenNewSalePanel = () => {
    this.openNewSalePanel = !this.openNewSalePanel;
  };

  handleMlsChange = (ev, option, index, value) => {
    this.mls = option.key;
  };

  handleAgentChanged = (e) => {
    const { inventory } = this.props;
    inventory.selectedAgent = e;
  };

  render() {
    const { inventory, history, t, intl, app, user } = this.props;
    if (inventory.redirect) {
      history.push(`/dashboard/${inventory.startedSale}`);
    }
    const canCreateBlankSale = app.features && app.features.createBlankSale;

    const commands = [];

    if (canCreateBlankSale) {
      commands.push({
        key: 'create',
        name: this.isTest ? t('Start new test sale') : t('Start new sale'),
        iconProps: {
          iconName: this.isTest ? 'TestBeaker' : 'Add',
        },
        onClick: this.handleOpenNewSalePanel,
        disabled: !user.hasReadWritePermissions,
      });
    }

    return (
      <Fragment>
        <div className="title">
          <a className="back" onClick={this.handleBack}>
            <i className="ms-Icon ms-Icon--NavigateBack" aria-hidden="true" />
            <span>{t('Back')}</span>
          </a>
          <span>{t('Add new listing')}</span>
        </div>

        {commands.length > 0 && (
          <div className="menu">
            <CommandBar items={commands} />
          </div>
        )}

        <div
          className={commands.length > 0 ? 'content' : 'content-without-menu'}
        >
          {inventory.fetching && (
            <div style={{ marginTop: 5 }}>
              <Loading label={t('Loading properties') + '...'} />
            </div>
          )}

          {!inventory.fetching &&
            inventory.items &&
            inventory.items.length > 0 && (
              <DetailsList
                columns={inventory.columns}
                items={inventory.items}
                selectionMode={1}
                checkboxVisibility={0}
                onRenderItemColumn={this.handleRenderObjectColumn}
                selection={this.selection}
                selectionPreservedOnEmptyClick={true}
              />
            )}

          {!inventory.fetching &&
            (!inventory.items || !inventory.items.length) && (
              <GuideOverlay theme={app.theme}>
                {inventory.error && (
                  <p style={{ color: 'Red' }}>{t('Error loading objects')}</p>
                )}
                {!inventory.error && (
                  <Fragment>
                    <p>{t('Guide no objects')}</p>
                    {canCreateBlankSale && (
                      <Link onClick={this.handleOpenNewSalePanel}>
                        <i
                          className="ms-Icon ms-Icon--CirclePlus"
                          aria-hidden="true"
                        ></i>{' '}
                        {t('Create new listing')}
                      </Link>
                    )}
                  </Fragment>
                )}
              </GuideOverlay>
            )}

          {(inventory.selectedItem || inventory.fetchingItem) && (
            <Panel
              headerText={
                this.isTest ? t('Start new test sale') : t('Start new sale')
              }
              type={PanelType.medium}
              isOpen={true}
              onRenderFooterContent={this.renderPanelFooterContent}
              isFooterAtBottom={true}
              onDismiss={() => inventory.deselect()}
            >
              {inventory.fetchingItem ? (
                <div style={{ marginTop: 5 }}>
                  <Loading label={t('Loading details') + '...'} />
                </div>
              ) : (
                <Fragment>
                  {this.isTest && (
                    <div className="ms-Grid-row">
                      <div className="ms-Grid-col ms-sm12 ms-lg12">
                        {t('test_sale_explanation')}
                      </div>
                    </div>
                  )}

                  <SearchPanelContent
                    item={inventory.selectedItem}
                    toc={this.toc}
                    intl={intl}
                    t={t}
                  />

                  {typeof inventory.selectedItem.docs !== 'undefined' &&
                    inventory.selectedItem.docs.length > 0 && (
                      <div className="ms-Grid">
                        <div className="ms-Grid-row">
                          <div className="ms-Grid-col ms-sm12 ms-lg3 ms-fontWeight-semibold">
                            {t('Documents')}:
                          </div>
                          <div className="ms-Grid-col ms-sm12 ms-lg9">
                            <Documents
                              documents={this.documents}
                              t={t}
                              onSelectionChanged={
                                this.handleDocumentSelectionChanged
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="ms-Grid">
                    <div className="ms-Grid-row">
                      <div className="ms-Grid-col ms-sm12 ms-lg12">
                        <BehalfOfAgentsDropdown
                          agents={inventory.agentsForSelectedItem}
                          selectedAgent={inventory.selectedAgent}
                          onAgentChanged={this.handleAgentChanged}
                        />
                      </div>
                    </div>
                  </div>

                  {app.canMls && (
                    <div className="ms-Grid">
                      <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12">
                          <Dropdown
                            label={t('MLS')}
                            selectedKey={this.mls}
                            onChange={this.handleMlsChange}
                            options={user.userConfig.mlsTypes.map((key) => {
                              return { key: key, text: t(`mls::${key}`) };
                            })}
                            required={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {app.tenantConfig.features.packageAuctions && (
                    <div className="ms-Grid">
                      <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12">
                          <Dropdown
                            label={t('Select master sale')}
                            selectedKey={this.masterSaleId}
                            onChange={(_, { key }) => (this.masterSaleId = key)}
                            options={[
                              { text: t('not part of master sale'), key: '' },
                              { text: t('is master sale'), key: 'master' },
                              ...inventory.masterSales,
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {app.tenantConfig.features.allowSelfRegistration &&
                    (!this.masterSaleId || this.masterSaleId === 'master') && (
                      <div className="ms-Grid">
                        <div className="ms-Grid-row">
                          <div className="ms-Grid-col ms-sm12">
                            <Checkbox
                              onChange={(_, checked) =>
                                (this.selfRegistration = checked)
                              }
                              value={this.selfRegistration}
                              label={t('Self registration')}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                </Fragment>
              )}
            </Panel>
          )}

          {this.openNewSalePanel && (
            <SaleCreatePanel
              isTest={this.isTest}
              onClose={() => (this.openNewSalePanel = false)}
            />
          )}
        </div>
      </Fragment>
    );
  }

  renderPanelFooterContent = () => {
    const { t, inventory, app, user } = this.props;
    return (
      <Observer
        render={() =>
          !inventory.fetchingItem && (
            <div className="ms-Grid">
              {inventory.isListed && (
                <div className="ms-Grid-row">
                  <div
                    className="ms-Grid-col ms-sm12"
                    style={{ color: '#DC1C2E' }}
                  >
                    {t('This object is already active')}
                  </div>
                </div>
              )}
              {inventory.saleAgent ? null : (
                <div className="ms-Grid-row">
                  <div
                    className="ms-Grid-col ms-sm12"
                    style={{ color: '#DC1C2E' }}
                  >
                    {t('Agent not found')}
                  </div>
                </div>
              )}
              <div className="ms-Grid-row">
                {!this.tocDisabled && (
                  <div
                    className="ms-Grid-col ms-sm12"
                    style={{ paddingTop: '15px' }}
                  >
                    <Checkbox
                      onChange={this.handleTocChange}
                      label={t('I agree with terms and conditions')}
                      onRenderLabel={() => (
                        <TermsLabel app={app} t={t} user={user} />
                      )}
                    />
                  </div>
                )}
                <div
                  className="ms-Grid-col ms-sm12"
                  style={{ paddingTop: '15px' }}
                >
                  <DefaultButton
                    onClick={() => {
                      if (!this.creating) inventory.deselect();
                      if (this.creationAbortController)
                        this.creationAbortController.abort();
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    {t('Cancel')}
                  </DefaultButton>
                  <PrimaryButton
                    onClick={this.handleAddNew}
                    disabled={
                      !user.hasReadWritePermissions ||
                      (!this.toc && !this.tocDisabled) ||
                      this.creating ||
                      !inventory.saleAgent
                    }
                  >
                    {this.creating
                      ? `${t('Creating')} (${this.processedDocumentsCount} / ${
                          this.selectedDocumentsCount
                        })...`
                      : t('Add')}
                    {this.creating && (
                      <Spinner
                        size={SpinnerSize.xSmall}
                        style={{ paddingLeft: '10px' }}
                      />
                    )}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )
        }
      />
    );
  };

  handleTocChange = (_, isChecked) => {
    this.toc = isChecked;
  };
}

export default Search;



// WEBPACK FOOTER //
// ./src/app/containers/Search/index.jsx